import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Bot, Loader2, RotateCcw, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });
const NONE = '__none__';

// Admin → Labor Bill: liga cada regra da IA (leitura do PDF) a um item/campo do Labor Bill (Designer)
export const AiFieldMappingCard = ({ onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keys, setKeys] = useState([]);
  const [items, setItems] = useState([]);
  const [map, setMap] = useState({});
  const [saved, setSaved] = useState({});

  const apply = useCallback((data) => {
    setKeys(data.keys || []);
    setItems(data.items || []);
    setMap(data.map || {});
    setSaved(data.map || {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/labor-bill/ai-field-mapping`, { headers: authHeaders() });
      apply(data);
    } catch (e) {
      toast.error('Não foi possível carregar a ligação IA → campo');
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { load(); }, [load]);

  const dirty = keys.some((k) => (map[k.key] || null) !== (saved[k.key] || null));
  const byField = Object.fromEntries(items.map((it) => [it.qty_field, it]));
  const sections = [...new Set(items.map((it) => it.section))];

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/ai-field-mapping`, { map }, { headers: authHeaders() });
      apply(data);
      onSaved?.(data.map || {});
      toast.success('Ligação IA → campo salva');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Voltar TODAS as ligações para o padrão do sistema?')) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai-field-mapping/reset`, {}, { headers: authHeaders() });
      apply(data);
      onSaved?.(data.map || {});
      toast.success('Ligações restauradas para o padrão');
    } catch (e) {
      toast.error('Erro ao restaurar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-4" data-testid="laborbill-ai-mapping-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="font-semibold text-slate-800 flex items-center gap-2"><Bot className="w-4 h-4 text-indigo-600" /> Ligação IA → campo do Labor Bill</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reset} disabled={saving || loading} data-testid="ai-mapping-reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Padrão
          </Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving || loading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ai-mapping-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Salvar
          </Button>
        </div>
      </div>
      <p className="text-sm text-slate-600 mb-3">
        Cada regra que a IA detecta no PDF preenche o item escolhido aqui. A ligação é pelo <b>campo</b> (não pelo nome): renomear ou mudar preço no Designer não quebra nada.
        Escolha <i>"— não preencher —"</i> para a IA ignorar aquela regra.
      </p>
      {loading ? (
        <div className="text-sm text-slate-500 py-3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
      ) : (
        <div className="space-y-1 max-h-[55vh] overflow-auto pr-1" data-testid="ai-mapping-list">
          {keys.map((k) => {
            const cur = map[k.key] || null;
            const isDefault = cur === (k.default_qty_field || null);
            const missing = cur && !byField[cur];
            return (
              <div key={k.key} className={`grid grid-cols-1 md:grid-cols-12 gap-2 items-center p-2 rounded ${isDefault ? 'hover:bg-slate-50' : 'bg-amber-50/60'}`} data-testid={`ai-mapping-row-${k.key}`}>
                <div className="md:col-span-5 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{k.label}</div>
                  <div className="text-[11px] text-slate-500 leading-tight">{k.detect}</div>
                </div>
                <div className="md:col-span-6">
                  <select
                    value={cur || NONE}
                    onChange={(e) => setMap((p) => ({ ...p, [k.key]: e.target.value === NONE ? null : e.target.value }))}
                    className={`w-full h-9 text-sm rounded-md border px-2 bg-white ${missing ? 'border-red-400 text-red-700' : 'border-slate-300'}`}
                    data-testid={`ai-mapping-select-${k.key}`}
                  >
                    <option value={NONE}>— não preencher —</option>
                    {missing && <option value={cur}>(campo removido do Designer: {cur})</option>}
                    {sections.map((sec) => (
                      <optgroup key={sec} label={sec}>
                        {items.filter((it) => it.section === sec).map((it) => (
                          <option key={it.qty_field} value={it.qty_field}>{it.description} — ${Number(it.unit_price || 0).toFixed(2)}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-1 text-right">
                  {!isDefault && (
                    <button
                      type="button"
                      className="text-[11px] text-indigo-600 hover:underline whitespace-nowrap"
                      onClick={() => setMap((p) => ({ ...p, [k.key]: k.default_qty_field || null }))}
                      title={`Padrão: ${byField[k.default_qty_field]?.description || k.default_qty_field || 'não preencher'}`}
                      data-testid={`ai-mapping-default-${k.key}`}
                    >
                      padrão
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {dirty && <p className="text-xs text-amber-700 mt-2" data-testid="ai-mapping-dirty">Alterações não salvas.</p>}
    </div>
  );
};

export default AiFieldMappingCard;
